import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import SuperAdminLayout from '../SuperAdminLayout';
import { saRequest } from '../../../utils/superAdminAxiosCaller';
import { parseSuperAdminError } from '../../../utils/superAdminErrors';
import ImpersonateModal, { ImpersonationTarget } from './ImpersonateModal';
import { SUPPORTED_LOCALES as ALL_LOCALES, LOCALE_META } from '../../../i18n/config';

type CompanyStatus = 'active' | 'suspended' | 'inactive';

interface Company {
    id: number;
    name: string;
    status: CompanyStatus;
    suspended_at: string | null;
    suspension_reason: string | null;
    scheduled_archive_at: string | null;
    inactive_at: string | null;
    inactive_reason: string | null;
    default_locale: string | null;
    active_locales: string[] | null;
    enabled_modules: string[] | null;
    max_users: number | null;
    kubit_enabled?: boolean;
    articles_import_enabled?: boolean;
    address: string | null;
    city: string | null;
    phone: string | null;
    email: string | null;
    fiscal_number: string | null;
    users_count?: number;
    created_at: string | null;
    updated_at: string | null;
}

interface TenantUser {
    id: number;
    username: string | null;
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    deleted_at: string | null;
    is_tenant_admin?: boolean;
}

// Must match App\Models\Company::MODULES server-side.
const ALL_MODULES: string[] = [
    'analytics',
    'contact', 'measures', 'offers', 'agreements', 'production',
    'warehouse', 'transport', 'installation', 'service', 'impression',
];

const SUPPORTED_LOCALES: { value: string; label: string }[] = ALL_LOCALES.map(code => ({
    value: code,
    label: `${LOCALE_META[code].native} (${code})`,
}));

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function StatusBadge({ status }: { status: CompanyStatus }) {
    return <span className={`sa-badge sa-badge-${status}`}>{status}</span>;
}

type LifecycleAction = 'suspend' | 'mark-inactive' | 'reactivate' | null;

const CompanyDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const [currentEmail, setCurrentEmail] = useState<string | null>(null);

    const [company, setCompany] = useState<Company | null>(null);
    const [users, setUsers] = useState<TenantUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Edit-mode state
    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDefaultLocale, setEditDefaultLocale] = useState('sq');
    const [editActiveLocales, setEditActiveLocales] = useState<string[]>([]);
    const [editAllModules, setEditAllModules] = useState(true);
    const [editEnabledModules, setEditEnabledModules] = useState<string[]>([]);
    const [editMaxUsers, setEditMaxUsers] = useState('');
    const [editKubitEnabled, setEditKubitEnabled] = useState(false);
    const [editArticlesImportEnabled, setEditArticlesImportEnabled] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editFieldErrors, setEditFieldErrors] = useState<Record<string, string>>({});

    // Lifecycle-action modal state
    const [actionModal, setActionModal] = useState<LifecycleAction>(null);
    const [actionReason, setActionReason] = useState('');
    const [actionSubmitting, setActionSubmitting] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    // Impersonation-target modal state
    const [impersonateTarget, setImpersonateTarget] = useState<ImpersonationTarget | null>(null);

    useEffect(() => {
        saRequest('GET', 'auth/me')
            .then(res => setCurrentEmail(res.data.super_admin?.email ?? null))
            .catch(() => {});
    }, []);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [cRes, uRes] = await Promise.all([
                saRequest<{ data: Company }>('GET', `companies/${id}`),
                saRequest<{ users: TenantUser[] }>('GET', `companies/${id}/users`),
            ]);
            setCompany(cRes.data.data);
            setUsers(uRes.data.users || []);
        } catch (err: any) {
            if (err?.response?.status === 401) {
                history.replace('/mika/ops/login');
                return;
            }
            if (err?.response?.status === 404) {
                setError('Company not found.');
                return;
            }
            const p = parseSuperAdminError(err);
            setError(p.banner ?? 'Failed to load company.');
        } finally {
            setLoading(false);
        }
    }, [id, history]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const beginEdit = () => {
        if (!company) return;
        setEditName(company.name || '');
        setEditDefaultLocale(company.default_locale || 'sq');
        setEditActiveLocales(company.active_locales || ['sq', 'en']);
        const isAll = company.enabled_modules == null;
        setEditAllModules(isAll);
        setEditEnabledModules(isAll ? ALL_MODULES.slice() : (company.enabled_modules || []));
        setEditMaxUsers(company.max_users != null ? String(company.max_users) : '');
        setEditKubitEnabled(Boolean(company.kubit_enabled));
        setEditArticlesImportEnabled(Boolean(company.articles_import_enabled));
        setEditFieldErrors({});
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setEditFieldErrors({});
    };

    const toggleActiveLocale = (loc: string) => {
        setEditActiveLocales(curr =>
            curr.includes(loc) ? curr.filter(l => l !== loc) : [...curr, loc],
        );
    };

    const toggleModule = (m: string) => {
        setEditEnabledModules(curr =>
            curr.includes(m) ? curr.filter(x => x !== m) : [...curr, m],
        );
    };

    const saveEdit = async () => {
        if (!company || editSubmitting) return;
        setEditSubmitting(true);
        setEditFieldErrors({});

        // If user has all modules checked AND the company currently has null (all enabled),
        // preserve the null to keep the "default all" semantic. Otherwise send the explicit array.
        const sendNullModules = editAllModules
            ? company.enabled_modules == null
            : false;

        const payload: Record<string, any> = {
            name: editName.trim(),
            default_locale: editDefaultLocale,
            active_locales: editActiveLocales,
            enabled_modules: sendNullModules ? null : editEnabledModules,
            // Empty input → null (unlimited); numeric → parsed int.
            max_users: editMaxUsers.trim() === '' ? null : Number(editMaxUsers.trim()),
            kubit_enabled: editKubitEnabled,
            articles_import_enabled: editArticlesImportEnabled,
        };

        try {
            const res = await saRequest<{ data: Company }>('PATCH', `companies/${id}`, payload);
            setCompany(res.data.data);
            setEditing(false);
        } catch (err: any) {
            if (err?.response?.status === 401) {
                history.replace('/mika/ops/login');
                return;
            }
            const p = parseSuperAdminError(err);
            if (p.banner) setError(p.banner);
            setEditFieldErrors(p.fields);
        } finally {
            setEditSubmitting(false);
        }
    };

    const openAction = (a: LifecycleAction) => {
        setActionModal(a);
        setActionReason('');
        setActionError(null);
    };

    const closeAction = () => {
        setActionModal(null);
        setActionReason('');
        setActionError(null);
    };

    const confirmAction = async () => {
        if (!company || !actionModal || actionSubmitting) return;
        setActionSubmitting(true);
        setActionError(null);

        const endpoint = actionModal; // maps to URL segment
        const body = actionModal === 'reactivate' ? {} : { reason: actionReason };

        try {
            const res = await saRequest<{ data: Company }>('POST', `companies/${id}/${endpoint}`, body);
            setCompany(res.data.data);
            closeAction();
        } catch (err: any) {
            if (err?.response?.status === 401) {
                history.replace('/mika/ops/login');
                return;
            }
            const p = parseSuperAdminError(err);
            setActionError(p.fields.reason ?? p.banner ?? 'Action failed.');
        } finally {
            setActionSubmitting(false);
        }
    };

    const editErr = (flat: string): string | undefined => editFieldErrors[flat];

    if (loading && !company) {
        return (
            <SuperAdminLayout currentEmail={currentEmail}>
                <div style={{ color: 'var(--sa-text-muted)', fontSize: 13 }}>Loading…</div>
            </SuperAdminLayout>
        );
    }

    if (error && !company) {
        return (
            <SuperAdminLayout currentEmail={currentEmail}>
                <div className="sa-card">
                    <div className="sa-error-banner">{error}</div>
                    <Link to="/mika/ops/companies" className="sa-btn sa-btn-secondary" style={{ textDecoration: 'none' }}>
                        ← Back to list
                    </Link>
                </div>
            </SuperAdminLayout>
        );
    }

    if (!company) return null;

    const canSuspend = company.status !== 'suspended';
    const canMarkInactive = company.status !== 'inactive';
    const canReactivate = company.status !== 'active';

    return (
        <SuperAdminLayout currentEmail={currentEmail}>
            <div style={{ marginBottom: 'var(--sa-s5)' }}>
                <Link
                    to="/mika/ops/companies"
                    style={{
                        color: 'var(--sa-text-tertiary)',
                        fontSize: 'var(--sa-fs-sm)',
                        textDecoration: 'none',
                        display: 'inline-block',
                        marginBottom: 'var(--sa-s3)',
                    }}
                >
                    ← Back to companies
                </Link>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sa-s3)', flexWrap: 'wrap' }}>
                    <h1 style={{ margin: 0, fontSize: 'var(--sa-fs-heading)', fontWeight: 600, letterSpacing: '-0.02em' }}>
                        {company.name}
                    </h1>
                    <StatusBadge status={company.status} />
                    <span className="sa-mono" style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-sm)' }}>
                        #{company.id}
                    </span>
                </div>
            </div>

            {error && <div className="sa-error-banner">{error}</div>}

            <div className="sa-detail-grid">
                {/* LEFT: details / edit form */}
                <div className="sa-card">
                    {!editing ? (
                        <>
                            <div className="sa-card-header">
                                <h2>Details</h2>
                                <button type="button" className="sa-btn sa-btn-secondary sa-btn-sm" onClick={beginEdit}>
                                    Edit
                                </button>
                            </div>

                            <dl className="sa-dl">
                                <dt>ID</dt><dd>#{company.id}</dd>
                                <dt>Name</dt><dd>{company.name}</dd>
                                <dt>Status</dt><dd><StatusBadge status={company.status} /></dd>
                                <dt>Default locale</dt><dd>{company.default_locale ?? '—'}</dd>
                                <dt>Active locales</dt><dd>{company.active_locales?.join(', ') ?? '—'}</dd>
                                <dt>Enabled modules</dt>
                                <dd>
                                    {company.enabled_modules === null ? (
                                        <em style={{ color: 'var(--sa-text-tertiary)' }}>All modules (default)</em>
                                    ) : company.enabled_modules.length === 0 ? (
                                        <em style={{ color: 'var(--sa-danger)' }}>None enabled</em>
                                    ) : (
                                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                            {company.enabled_modules.map(m => (
                                                <span
                                                    key={m}
                                                    className="sa-mono"
                                                    style={{
                                                        fontSize: 'var(--sa-fs-xs)',
                                                        padding: '2px 8px',
                                                        background: 'var(--sa-surface)',
                                                        border: '1px solid var(--sa-border)',
                                                        borderRadius: 'var(--sa-radius-sm)',
                                                        color: 'var(--sa-text)',
                                                    }}
                                                >
                                                    {m}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </dd>
                                <dt>Users</dt>
                                <dd>
                                    {company.users_count ?? users.length}
                                    {company.max_users != null && (
                                        <span style={{ color: 'var(--sa-text-muted)' }}> of {company.max_users} seats</span>
                                    )}
                                </dd>
                                <dt>User seat limit</dt>
                                <dd>
                                    {company.max_users == null
                                        ? <em style={{ color: 'var(--sa-text-muted)' }}>Unlimited</em>
                                        : company.max_users}
                                </dd>
                                <dt>KuBIT integration</dt>
                                <dd>
                                    {company.kubit_enabled
                                        ? <span style={{ color: 'var(--sa-green, #16a34a)' }}>Enabled</span>
                                        : <em style={{ color: 'var(--sa-text-muted)' }}>Disabled</em>}
                                </dd>
                                <dt>Article import</dt>
                                <dd>
                                    {company.articles_import_enabled
                                        ? <span style={{ color: 'var(--sa-green, #16a34a)' }}>Enabled</span>
                                        : <em style={{ color: 'var(--sa-text-muted)' }}>Disabled</em>}
                                </dd>
                                <dt>Created</dt><dd>{formatDate(company.created_at)}</dd>
                                <dt>Updated</dt><dd>{formatDate(company.updated_at)}</dd>

                                {company.status === 'suspended' && (
                                    <>
                                        <dt>Suspended at</dt><dd>{formatDate(company.suspended_at)}</dd>
                                        <dt>Suspension reason</dt><dd>{company.suspension_reason ?? '—'}</dd>
                                        <dt>Scheduled archive</dt><dd>{formatDate(company.scheduled_archive_at)}</dd>
                                    </>
                                )}
                                {company.status === 'inactive' && (
                                    <>
                                        <dt>Inactive at</dt><dd>{formatDate(company.inactive_at)}</dd>
                                        <dt>Inactive reason</dt><dd>{company.inactive_reason ?? '—'}</dd>
                                    </>
                                )}
                            </dl>

                            {(company.address || company.city || company.phone || company.email || company.fiscal_number) && (
                                <>
                                    <hr className="sa-hairline" style={{ margin: 'var(--sa-s5) 0 var(--sa-s3)' }} />
                                    <div className="sa-section-label" style={{ margin: '0 0 var(--sa-s3)' }}>Tenant profile</div>
                                    <dl className="sa-dl">
                                        {company.address && (<><dt>Address</dt><dd>{company.address}</dd></>)}
                                        {company.city && (<><dt>City</dt><dd>{company.city}</dd></>)}
                                        {company.phone && (<><dt>Phone</dt><dd className="sa-mono">{company.phone}</dd></>)}
                                        {company.email && (<><dt>Email</dt><dd className="sa-mono">{company.email}</dd></>)}
                                        {company.fiscal_number && (<><dt>Fiscal number</dt><dd className="sa-mono">{company.fiscal_number}</dd></>)}
                                    </dl>
                                    <p className="sa-field-help" style={{ marginTop: 'var(--sa-s3)' }}>
                                        Tenant profile fields are edited from the tenant admin panel, not here.
                                    </p>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <h2 style={{ margin: '0 0 16px', fontSize: 16 }}>Edit company</h2>

                            <div className="sa-field">
                                <label>Name</label>
                                <input type="text" value={editName} onChange={e => setEditName(e.target.value)} disabled={editSubmitting} />
                                {editErr('name') && <div className="sa-field-error">{editErr('name')}</div>}
                            </div>

                            <div className="sa-field">
                                <label>Default locale</label>
                                <select value={editDefaultLocale} onChange={e => setEditDefaultLocale(e.target.value)} disabled={editSubmitting}>
                                    {SUPPORTED_LOCALES.map(l => (
                                        <option key={l.value} value={l.value}>{l.label}</option>
                                    ))}
                                </select>
                                {editErr('default_locale') && <div className="sa-field-error">{editErr('default_locale')}</div>}
                            </div>

                            <div className="sa-field">
                                <label>Active locales</label>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                    {SUPPORTED_LOCALES.map(l => (
                                        <label key={l.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={editActiveLocales.includes(l.value)}
                                                onChange={() => toggleActiveLocale(l.value)}
                                                disabled={editSubmitting}
                                            />
                                            <span>{l.label}</span>
                                        </label>
                                    ))}
                                </div>
                                {editErr('active_locales') && <div className="sa-field-error">{editErr('active_locales')}</div>}
                            </div>

                            <div className="sa-field">
                                <label>Enabled modules</label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={editAllModules}
                                        onChange={e => {
                                            setEditAllModules(e.target.checked);
                                            if (e.target.checked) setEditEnabledModules(ALL_MODULES.slice());
                                        }}
                                        disabled={editSubmitting}
                                    />
                                    <span>All modules enabled (default)</span>
                                </label>
                                {!editAllModules && (
                                    <div className="sa-module-grid">
                                        {ALL_MODULES.map(m => (
                                            <label key={m}>
                                                <input
                                                    type="checkbox"
                                                    checked={editEnabledModules.includes(m)}
                                                    onChange={() => toggleModule(m)}
                                                    disabled={editSubmitting}
                                                />
                                                <span>{m}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                {editErr('enabled_modules') && <div className="sa-field-error">{editErr('enabled_modules')}</div>}
                            </div>

                            <div className="sa-field">
                                <label>User seat limit</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={100000}
                                    value={editMaxUsers}
                                    onChange={e => setEditMaxUsers(e.target.value)}
                                    disabled={editSubmitting}
                                    placeholder="Leave blank for unlimited"
                                />
                                {editErr('max_users') && <div className="sa-field-error">{editErr('max_users')}</div>}
                                {company.users_count != null && editMaxUsers.trim() !== '' && Number(editMaxUsers) < company.users_count && (
                                    <div style={{ fontSize: 11, color: 'var(--sa-amber)', marginTop: 4 }}>
                                        Note: current user count ({company.users_count}) exceeds this limit. Existing users stay; new user creation will be blocked until someone is removed or the limit is raised.
                                    </div>
                                )}
                            </div>

                            <div className="sa-field">
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={editKubitEnabled}
                                        onChange={e => setEditKubitEnabled(e.target.checked)}
                                        disabled={editSubmitting}
                                    />
                                    <span>KuBIT integration enabled</span>
                                </label>
                                <div style={{ fontSize: 11, color: 'var(--sa-text-muted)', marginTop: 4 }}>
                                    When on, this tenant's admins see the KuBIT integration page under <code>/admin/integrations/kubit</code>. Default off; flip on for tenants who have an active KuBIT subscription.
                                </div>
                                {editErr('kubit_enabled') && <div className="sa-field-error">{editErr('kubit_enabled')}</div>}
                            </div>

                            <div className="sa-field">
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={editArticlesImportEnabled}
                                        onChange={e => setEditArticlesImportEnabled(e.target.checked)}
                                        disabled={editSubmitting}
                                    />
                                    <span>Article Excel import enabled</span>
                                </label>
                                <div style={{ fontSize: 11, color: 'var(--sa-text-muted)', marginTop: 4 }}>
                                    When on, this tenant's admins see the bulk article Excel import button on the Articles page. Default off; flip on for tenants who need to import their catalogue.
                                </div>
                                {editErr('articles_import_enabled') && <div className="sa-field-error">{editErr('articles_import_enabled')}</div>}
                            </div>

                            <div style={{ display: 'flex', gap: 'var(--sa-s2)', marginTop: 'var(--sa-s4)' }}>
                                <button type="button" className="sa-btn sa-btn-primary" onClick={saveEdit} disabled={editSubmitting || !editName.trim()}>
                                    {editSubmitting && <span className="sa-btn-spinner" />}
                                    {editSubmitting ? 'Saving…' : 'Save changes'}
                                </button>
                                <button type="button" className="sa-btn sa-btn-secondary" onClick={cancelEdit} disabled={editSubmitting}>
                                    Cancel
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* RIGHT: lifecycle actions + users */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sa-s4)' }}>
                    <div className="sa-card">
                        <div className="sa-card-header">
                            <h2>Lifecycle</h2>
                        </div>
                        <div className="sa-action-buttons">
                            {canReactivate && (
                                <button
                                    type="button"
                                    className="sa-btn sa-btn-primary"
                                    onClick={() => openAction('reactivate')}
                                    disabled={editing}
                                >
                                    Reactivate
                                </button>
                            )}
                            {canSuspend && (
                                <button
                                    type="button"
                                    className="sa-btn sa-btn-danger"
                                    onClick={() => openAction('suspend')}
                                    disabled={editing}
                                >
                                    Suspend
                                </button>
                            )}
                            {canMarkInactive && (
                                <button
                                    type="button"
                                    className="sa-btn sa-btn-danger"
                                    onClick={() => openAction('mark-inactive')}
                                    disabled={editing}
                                >
                                    Mark inactive
                                </button>
                            )}
                        </div>
                        <p className="sa-field-help" style={{ marginTop: 'var(--sa-s3)' }}>
                            <strong style={{ color: 'var(--sa-text-secondary)' }}>Suspend</strong> — tenants can't log in; data preserved.{' '}
                            <strong style={{ color: 'var(--sa-text-secondary)' }}>Inactive</strong> — long-term stop.
                            Both reversible via Reactivate.
                        </p>
                    </div>

                    <div className="sa-card">
                        <div className="sa-card-header">
                            <h2>
                                Users <span className="sa-mono" style={{ color: 'var(--sa-text-tertiary)', fontWeight: 400, fontSize: 'var(--sa-fs-sm)' }}>
                                    {users.length}{company.max_users != null ? ` / ${company.max_users}` : ''}
                                </span>
                            </h2>
                        </div>
                        {users.length === 0 ? (
                            <div className="sa-empty" style={{ padding: 'var(--sa-s6) var(--sa-s2)' }}>
                                <div className="sa-empty-title" style={{ fontSize: 'var(--sa-fs-body)' }}>No users yet</div>
                                <div className="sa-empty-hint" style={{ fontSize: 'var(--sa-fs-xs)' }}>
                                    The tenant's admin can add users from their own admin panel.
                                </div>
                            </div>
                        ) : (
                            <div style={{ maxHeight: 320, overflowY: 'auto', margin: 'calc(var(--sa-s2) * -1) 0' }}>
                                {users.map((u, i) => {
                                    const isDeleted = u.deleted_at !== null;
                                    const canImpersonate = !isDeleted && company.status === 'active';
                                    const fullname = (u.firstname || u.lastname)
                                        ? `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim()
                                        : (u.username ?? '—');
                                    return (
                                        <div
                                            key={u.id}
                                            style={{
                                                padding: 'var(--sa-s3) 0',
                                                borderTop: i === 0 ? 'none' : '1px solid var(--sa-border)',
                                                fontSize: 'var(--sa-fs-sm)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 'var(--sa-s3)',
                                                opacity: isDeleted ? 0.5 : 1,
                                            }}
                                        >
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ color: 'var(--sa-text)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fullname}</span>
                                                    {u.is_tenant_admin && (
                                                        <span
                                                            style={{
                                                                fontSize: 10,
                                                                padding: '1px 6px',
                                                                borderRadius: 'var(--sa-radius-sm)',
                                                                background: 'var(--sa-accent-soft)',
                                                                color: 'var(--sa-accent)',
                                                                fontWeight: 600,
                                                                letterSpacing: '0.04em',
                                                                textTransform: 'uppercase',
                                                            }}
                                                        >
                                                            Admin
                                                        </span>
                                                    )}
                                                    {isDeleted && (
                                                        <span
                                                            style={{
                                                                fontSize: 10,
                                                                padding: '1px 6px',
                                                                borderRadius: 'var(--sa-radius-sm)',
                                                                background: 'var(--sa-danger-soft)',
                                                                color: 'var(--sa-danger)',
                                                                fontWeight: 600,
                                                                letterSpacing: '0.04em',
                                                                textTransform: 'uppercase',
                                                            }}
                                                        >
                                                            Deleted
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="sa-mono" style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-xs)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {u.email ?? ''}{u.username ? ` · @${u.username}` : ''}
                                                </div>
                                            </div>
                                            {canImpersonate && (
                                                <button
                                                    type="button"
                                                    className="sa-btn sa-btn-secondary sa-btn-sm"
                                                    onClick={() => setImpersonateTarget({
                                                        user_id: u.id,
                                                        user_email: u.email,
                                                        user_fullname: fullname,
                                                        company_id: company.id,
                                                        company_name: company.name,
                                                        is_tenant_admin: !!u.is_tenant_admin,
                                                    })}
                                                    title="Open a read-only impersonation session"
                                                >
                                                    Impersonate
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {impersonateTarget && (
                <ImpersonateModal
                    target={impersonateTarget}
                    onClose={() => setImpersonateTarget(null)}
                />
            )}

            {/* Lifecycle action modal */}
            {actionModal && (
                <div className="sa-modal-backdrop" onClick={closeAction}>
                    <div className="sa-modal" onClick={e => e.stopPropagation()}>
                        <h2>
                            {actionModal === 'suspend' && <>Suspend <span style={{ color: 'var(--sa-accent)' }}>{company.name}</span>?</>}
                            {actionModal === 'mark-inactive' && <>Mark <span style={{ color: 'var(--sa-accent)' }}>{company.name}</span> as inactive?</>}
                            {actionModal === 'reactivate' && <>Reactivate <span style={{ color: 'var(--sa-accent)' }}>{company.name}</span>?</>}
                        </h2>

                        {actionModal === 'suspend' && (
                            <p style={{ color: 'var(--sa-text-secondary)', fontSize: 'var(--sa-fs-sm)', margin: '0 0 var(--sa-s3)' }}>
                                Tenants will be blocked from logging in. Active sessions are revoked immediately.
                                Data is preserved and this is fully reversible.
                            </p>
                        )}
                        {actionModal === 'mark-inactive' && (
                            <p style={{ color: 'var(--sa-text-secondary)', fontSize: 'var(--sa-fs-sm)', margin: '0 0 var(--sa-s3)' }}>
                                Tenants can no longer log in. Meant as a long-term marker (churn, wind-down).
                                Data preserved. Reversible.
                            </p>
                        )}
                        {actionModal === 'reactivate' && (
                            <p style={{ color: 'var(--sa-text-secondary)', fontSize: 'var(--sa-fs-sm)', margin: '0 0 var(--sa-s3)' }}>
                                Tenants can log in again. All suspension and inactive metadata gets cleared.
                            </p>
                        )}

                        {actionError && <div className="sa-error-banner">{actionError}</div>}

                        {actionModal !== 'reactivate' && (
                            <div className="sa-field" style={{ marginBottom: 0 }}>
                                <label>Reason <span style={{ color: 'var(--sa-text-tertiary)', fontWeight: 400, textTransform: 'none' }}>— required, min 5 chars, shown in audit log</span></label>
                                <textarea
                                    rows={3}
                                    value={actionReason}
                                    onChange={e => setActionReason(e.target.value)}
                                    disabled={actionSubmitting}
                                    placeholder="e.g. non-payment March 2026, customer requested pause"
                                    autoFocus
                                />
                            </div>
                        )}

                        <div className="sa-modal-actions">
                            <button type="button" className="sa-btn sa-btn-ghost" onClick={closeAction} disabled={actionSubmitting}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`sa-btn ${actionModal === 'reactivate' ? 'sa-btn-primary' : 'sa-btn-danger'}`}
                                onClick={confirmAction}
                                disabled={
                                    actionSubmitting ||
                                    (actionModal !== 'reactivate' && actionReason.trim().length < 5)
                                }
                            >
                                {actionSubmitting && <span className="sa-btn-spinner" />}
                                {actionSubmitting ? 'Working…' :
                                    actionModal === 'suspend' ? 'Suspend tenant' :
                                    actionModal === 'mark-inactive' ? 'Mark as inactive' :
                                    'Reactivate tenant'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </SuperAdminLayout>
    );
};

export default CompanyDetailPage;
